import "mapbox-gl/dist/mapbox-gl.css";
import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import Papa from "papaparse";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";

const MapperPage = () => {
  const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

  mapboxgl.accessToken = accessToken;

  const [map, setMap] = useState(null);
  const [points, setPoints] = useState([]);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);

  const zipInputRef = useRef();

  const handleMarkerClick = (point) => {
    setSelectedPoint(point);
    setDrawerVisible(true);
  };

  const handleCloseDrawer = () => {
    setSelectedPoint(null);
    setDrawerVisible(false);
  };

  const handleSearch = async () => {
    toast.dismiss();
    try {
      const zipCode = zipInputRef.current.value;

      // const foundPoint = points.find((point) => point.zip === zipCode);
      // if (foundPoint) {
      // Zip code found, proceed with updating the map
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${zipCode}.json?access_token=${accessToken}`
      );
      const data = await response.json();

      if (data.features.length > 0) {
        const coordinates = data.features[0].center;
        map.flyTo({ center: coordinates, zoom: 8 });
      } else {
        console.error("No location found for the entered ZIP code.");
        toast.error("No location found for the entered ZIP code.", {
          autoClose: 3000,
        });
      }
      // } else {
      //   // Zip code not found, display error message
      //   toast.error("Zip code not found", { autoClose: 3000 });
      // }
    } catch (error) {
      console.error("Error searching:", error);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  useEffect(() => {
    const fetchDataFromCSV = async () => {
      Papa.parse("/mapper.csv", {
        download: true,
        header: true,
        complete: (result) => {
          const newPoints = result.data
            .filter((row) => row["Include in the Mapper?"].toLowerCase() === "x")
            .filter((row) => !isNaN(parseFloat(row.Longitude)) && !isNaN(parseFloat(row.Latitude)))
            .map((row) => ({
              lng: parseFloat(row.Longitude),
              lat: parseFloat(row.Latitude),
              clinicianDegrees:
                row["Name of board-certified Lifestyle Medicine clinician and degrees. (This will be verified.)"],
              speciality: row.Specialty,
              acceptingPatients: row["Accepting new patients?"],
              acceptedAges: row["Ages Accepted"],
              organization: row["Name of practice or healthcare organization. "],
              website: row["URL or website link"],
              phone: row["Phone Number"],
              address: row["Address (required, even if only telemedicine)"],
              telemedicine: row.Telemedicine,
              acceptingInsurance: row["Accepting insurance? "],
              zip: row["Zip Code"],
            }));
          setPoints(newPoints);
        },
        error: (error) => {
          console.error("Error parsing CSV:", error);
        },
      });
    };

    fetchDataFromCSV();
  }, []);

  useEffect(() => {
    const mapInstance = new mapboxgl.Map({
      container: "map",
      style: "mapbox://styles/mapbox/streets-v12",
      center: [-103, 40],
      zoom: 3,
    });

    mapInstance.on("load", () => {
      setMap(mapInstance);
    });

    points.forEach((point) => {
      const marker = new mapboxgl.Marker().setLngLat([point.lng, point.lat]).addTo(mapInstance);

      marker.getElement().addEventListener("click", () => {
        handleMarkerClick(point);
      });
    });

    return () => mapInstance.remove();
  }, [points]);

  const DetailItem = ({ title, value, isUrl = false }) => {
    return (
      <>
        {value && (
          <div>
            <h3 className="text-sm text-slate-500/70 mb-0">{title}</h3>
            {isUrl ? <a href={value}>{value}</a> : <p className="text-slate-700">{value}</p>}
          </div>
        )}
      </>
    );
  };

  const SectionItem = ({ title, children }) => {
    return (
      <div className="grid lg:grid-cols-3 xl:grid-cols-4 gap-4 py-8">
        <h2 className="col-span-1 text-lg lg:text-xl">{title}</h2>
        <div className="col-span-2 xl:col-span-3 text-slate-500">{children}</div>
      </div>
    );
  };

  return (
    <div className="pt-8">
      {/* Overlay */}
      {selectedPoint && (
        <div
          className="fixed top-0 left-0 w-screen h-screen overflow-hidden z-[10] block bg-slate-800/30"
          onClick={handleCloseDrawer}
        ></div>
      )}

      {/* Drawer */}
      <div
        className={`z-[11] fixed top-0 right-0 w-[90vw] sm:w-1/2 lg:w-1/3 h-full bg-white shadow-lg overflow-y-auto transform transition-transform duration-300 ${
          drawerVisible ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div>
          {selectedPoint && (
            <>
              <div className="flex justify-between border-b border-slate-200 p-4">
                <p>Practice details</p>
                <button onClick={handleCloseDrawer} className="focus:outline-none">
                  <XMarkIcon className="w-6 h-6 text-slate-700 hover:bg-slate-200 rounded transition" />
                </button>
              </div>
              <div className="p-4">
                <div className="flex flex-col gap-2.5">
                  <DetailItem
                    title="Name of board-certified Lifestyle Medicine clinician and degrees"
                    value={selectedPoint?.clinicianDegrees}
                  />
                  <DetailItem title="Speciality" value={selectedPoint?.speciality} />
                  <DetailItem title="Accepting new patients" value={selectedPoint?.acceptingPatients} />
                  <DetailItem title="Ages accepted" value={selectedPoint?.acceptedAges} />
                  <DetailItem title="Name of practice or healthcare organization" value={selectedPoint?.organization} />
                  <DetailItem title="URL or website link" value={selectedPoint?.website} isUrl={true} />
                  <DetailItem title="Phone Number" value={selectedPoint?.phone} />
                  <DetailItem title="Address" value={selectedPoint?.address} />
                  <DetailItem title="Telemedicine" value={selectedPoint?.telemedicine} />
                  <DetailItem title="Accepting Insurance" value={selectedPoint?.acceptingInsurance} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="px-6">
        {/* Top Text */}
        <div className="text-center lg:w-1/2 mx-auto mb-5">
          <h1 className="text-lg lg:text-xl">Lifestyle Medicine Mapper</h1>
          <p className="text-slate-500">
            This map shows {points?.length} certified Lifestyle Medicine practitioners. Please search by your zip code
            and click on a pin to read more information about each practice.
          </p>
        </div>

        {/* Map */}
        <div id="map" className="map-container h-[40vh] md:h-[400px] lg:h-[500px] xl:h-[600px] rounded-lg relative">
          <div className="absolute top-4 right-4 z-[9]">
            <div className="relative">
              <input
                type="search"
                id="default-search"
                className="block w-[70vw] sm:w-96 py-2.5 px-3 text-sm text-gray-900 border border-gray-500 rounded-lg bg-white focus:outline-none"
                placeholder="Search by zip or code postal, e.g 12345"
                ref={zipInputRef}
                onKeyDown={handleKeyDown}
              />
              <button
                type="button"
                className="text-white absolute end-[0.35rem] bottom-[0.35rem] bg-gray-700 hover:bg-gray-800 focus:ring-0 focus:outline-none font-medium rounded-lg text-sm px-3.5 py-1.5"
                onClick={handleSearch}
              >
                Search
              </button>
            </div>
          </div>
        </div>

        {/* Sections */}
        <SectionItem title="What is Lifestyle Medicine?">
          <p className="mb-2">
            Lifestyle Medicine is a newer field of medicine that prioritizes evidence-based, therapeutic lifestyle
            changes over pharmaceuticals and surgeries, when possible. There are six pillars of Lifestyle Medicine:
          </p>
          <ul>
            <li>- Diet and Nutrition</li>
            <li>- Physical and Mental Activity</li>
            <li>- Sleep</li>
            <li>- Emotional Well-being</li>
            <li>- Avoiding Toxic Substances and Environments</li>
            <li>- Social Connectedness</li>
          </ul>
          <p>
            Each pillar has significant evidence that can help guide people towards a healthier, longer life. If this
            sounds like something you'd be interested in, use the map to find a practice near you!{" "}
            <a className="underline" href="https://lifestylemedicine.org/">
              Read more about Lifestyle Medicine
            </a>
            .
          </p>
        </SectionItem>

        <hr className="h-[2px] bg-slate-200 border-0" />

        <SectionItem title="Add your practice">
          <p className="mb-2">Currently, we are only displaying board certified Lifestyle Medicine practices.</p>
          <p className="my-5 py-3 flex justify-center items-center">
            <a
              href="https://forms.gle/UrkXMrvuZEEtwmWC8"
              className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              Add Your Practice To the Map
            </a>{" "}
          </p>
          <p className="mt-2">
            We will be confirming each submission before adding it to the form. If you'd like to learn more about
            getting certified for Lifestyle Medicine, please visit this site:{" "}
            <a href="https://ablm.org/">https://ablm.org/</a>
          </p>
        </SectionItem>
      </div>
    </div>
  );
};

export default MapperPage;
